// In-page replacement for window.confirm / alert / prompt (#4197).
//
// The native dialogs block the whole tab — they froze a browser-automation
// session on 2026-09-25 and CLAUDE.md §5 forbids them. This hook gives each
// component its own MUI dialog and a promise-returning API, so call sites keep
// their shape:
//
//   const [dialogs, dialogUi] = useConfirm();
//   if (!(await dialogs.confirm({ title: 'Delete request?', message, confirmLabel: 'Delete', danger: true }))) return;
//   const typed = await dialogs.prompt({ title, message, label: 'Count' });   // string | null
//   await dialogs.alert({ title: "Couldn't parse the proposal", message });
//   ...
//   return <>{...}{dialogUi}</>;
//
// No provider: the dialog renders wherever the component puts `dialogUi`.
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField,
} from '@mui/material';

export function useConfirm() {
    const [req, setReq] = useState(null);   // { kind, title, message, confirmLabel, cancelLabel, danger, label, defaultValue }
    const [value, setValue] = useState('');
    const resolver = useRef(null);

    const open = useCallback((kind, opts = {}) => new Promise((resolve) => {
        // A second request while one is open resolves the first as cancelled.
        if (resolver.current) resolver.current(kind === 'prompt' ? null : false);
        resolver.current = resolve;
        setValue(opts.defaultValue ?? '');
        setReq({ kind, ...opts });
    }), []);

    const close = useCallback((result) => {
        const r = resolver.current;
        resolver.current = null;
        setReq(null);
        if (r) r(result);
    }, []);

    const dialogs = useMemo(() => ({
        confirm: (opts) => open('confirm', opts),
        prompt: (opts) => open('prompt', opts),
        alert: (opts) => open('alert', opts),
    }), [open]);

    const cancelResult = req?.kind === 'prompt' ? null : false;
    const okResult = req?.kind === 'prompt' ? value : true;

    // React events bubble out of a portal to the component that renders it, so a
    // click on "Confirm" would also reach e.g. the to-do row's expand handler.
    const stop = (e) => e.stopPropagation();
    const dialogUi = (
        <span style={{ display: 'contents' }} onClick={stop} onMouseDown={stop} onKeyDown={stop} onSubmit={stop}>
        <Dialog
            open={!!req}
            onClose={() => close(cancelResult)}
            maxWidth="xs"
            fullWidth
            aria-labelledby="use-confirm-title"
        >
            {req && (
                <form onSubmit={(e) => { e.preventDefault(); close(okResult); }}>
                    <DialogTitle id="use-confirm-title">{req.title || (req.kind === 'alert' ? 'Notice' : 'Are you sure?')}</DialogTitle>
                    <DialogContent>
                        {req.message && (
                            <DialogContentText component="div" sx={{ whiteSpace: 'pre-line' }}>{req.message}</DialogContentText>
                        )}
                        {req.kind === 'prompt' && (
                            <TextField
                                autoFocus
                                fullWidth
                                size="small"
                                margin="dense"
                                label={req.label}
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                inputProps={{ 'aria-label': req.label || 'Value' }}
                            />
                        )}
                    </DialogContent>
                    <DialogActions>
                        {req.kind !== 'alert' && (
                            <Button onClick={() => close(cancelResult)} color="inherit">{req.cancelLabel || 'Cancel'}</Button>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            color={req.danger ? 'error' : 'primary'}
                            autoFocus={req.kind !== 'prompt'}
                        >
                            {req.confirmLabel || (req.kind === 'alert' ? 'OK' : 'Confirm')}
                        </Button>
                    </DialogActions>
                </form>
            )}
        </Dialog>
        </span>
    );

    return [dialogs, dialogUi];
}

export default useConfirm;
